import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { supabase } from "../supabase";
import { FaLocationDot } from "react-icons/fa6";
import {
  onAuthStateChanged,
  RecaptchaVerifier,
  signInWithPhoneNumber,
} from "firebase/auth";
import { auth, googleProvider, db } from "../firebase";
import { Timestamp } from "firebase/firestore";
import { useJsApiLoader } from "@react-google-maps/api";
import GoToTop from "../GoToTop";
import {
  addDoc,
  doc,
  serverTimestamp,
  collection,
  onSnapshot,
  setDoc,
  getDoc,
  query,
  where,
} from "firebase/firestore";
import { formatINR } from "../utils/currency";
import { toast } from "react-toastify";
import ReserveTripModal from "./ReserveTripModal";
import {
  RAZORPAY_KEY,
  SMS_BASE_URL,
  SMS_FROM_NUMBER,
  SMS_API_KEY,MAP_KEY
} from "../API_KEYS";

function ChooseCar() {
  const location = useLocation();
  const navigate = useNavigate();

  const [directionsResult, setDirectionsResult] = useState(null);

  const pickup = location.state?.pickup;
  const drop = location.state?.drop;
  const isScheduled = location.state?.isScheduled;
  const pickupDate = location.state?.pickupDate;
  const pickupTime = location.state?.pickupTime;
  const dropDate = location.state?.dropDate;

  // console.log(pickupDate, "ddd")
  const [showEditModal, setShowEditModal] = useState(false);
  const [updatingTrip, setUpdatingTrip] = useState(false);
  const [loading, setLoading] = useState(false);

  const [pickupCoords, setPickupCoords] = useState(null);
  const [dropCoords, setDropCoords] = useState(null);

  const [distance, setDistance] = useState(null);
  const [duration, setDuration] = useState(null);

  const [vehicles, setVehicles] = useState([]);
  const [selectedCar, setSelectedCar] = useState(null);

  const [showBookingModal, setShowBookingModal] = useState(false);
  const [customerName, setCustomerName] = useState("");
  const [customerPhone, setCustomerPhone] = useState("");
  const [passengers, setPassengers] = useState(1);
  const [savingBooking, setSavingBooking] = useState(false);

  const [vehicleStatusMap, setVehicleStatusMap] = useState({});

  const [scheduledBookings, setScheduledBookings] = useState([]);

  const [showStateModal, setShowStateModal] = useState(false);
  const [manualState, setManualState] = useState("");

  const [user, setUser] = useState(null);

  // AUTH USER
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (currentUser) => {
      if (!currentUser) return setUser(null);

      try {
        const userDocRef = doc(db, "Users", currentUser.uid);
        const userDocSnap = await getDoc(userDocRef);

        if (userDocSnap.exists()) {
          const userData = userDocSnap.data();
          setUser(userData);
          setCustomerName(userData.name || "");
          setCustomerPhone(userData.phone || "");
        } else {
          setUser(currentUser);
          setCustomerName(currentUser.displayName || "");
          setCustomerPhone(currentUser.phoneNumber || "");
        }
      } catch {
        setUser(currentUser);
      }
    });

    return () => unsub();
  }, []);

  const fetchUserData = async (uid) => {
    const userDocRef = doc(db, "Users", uid);
    const userDocSnap = await getDoc(userDocRef);

    if (userDocSnap.exists()) {
      const userData = userDocSnap.data();
      setUser(userData);
      setCustomerName(userData?.name || "");
    }
  };

  const [dropState, setDropState] = useState(null);

  const extractStateFromDropText = (dropText) => {
    if (!dropText) return null;

    const states = [
      "Assam",
      "Arunachal Pradesh",
      "Bihar",
      "Meghalaya",
      "Manipur",
      "Mizoram",
      "Nagaland",
      "Tripura",
      "West Bengal",
    ];

    const matchedState = states.find((state) =>
      dropText.toLowerCase().includes(state.toLowerCase()),
    );

    return matchedState || null;
  };

  // LOAD VEHICLES
  useEffect(() => {
    const loadVehicles = async () => {
      const { data: cars, error } = await supabase
        .from("Vehicles")
        .select("*")
        .order("daily_price", { ascending: true });

      if (!error && cars) {
        setVehicles(cars);

        if (!selectedCar && cars.length > 0) {
          setSelectedCar(cars[0]);
        }
      }
    };

    loadVehicles();
  }, []);

  useEffect(() => {
    const q = query(
      collection(db, "LiveBookings"),
      where("isScheduled", "==", true),
    );

    const unsub = onSnapshot(q, (snapshot) => {
      const scheduled = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      setScheduledBookings(scheduled);
    });

    return () => unsub();
  }, []);

  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: MAP_KEY, // Replace with your key
  });

  useEffect(() => {
    if (filteredVehicles.length > 0) {
      setSelectedCar(filteredVehicles[0]);
    } else {
      setSelectedCar(null);
    }
  }, [vehicleStatusMap, vehicles]);

  // Convert address to coords using Google Maps
  const getCoords = async (place, setter) => {
    if (!window.google) return;

    const geocoder = new window.google.maps.Geocoder();

    geocoder.geocode({ address: place }, (results, status) => {
      if (status === "OK" && results[0]) {
        const loc = results[0].geometry.location;
        setter([loc.lat(), loc.lng()]);
      } else {
        console.error("Google Geocode failed:", status);
      }
    });
  };

  useEffect(() => {
    const unsub = onSnapshot(collection(db, "Vehicles"), (snapshot) => {
      const statusData = {};

      snapshot.forEach((docSnap) => {
        statusData[docSnap.id] = docSnap.data().status;
      });

      setVehicleStatusMap(statusData);
    });

    return () => unsub();
  }, []);

  useEffect(() => {
    if (pickup) getCoords(pickup, setPickupCoords);
    if (drop) getCoords(drop, setDropCoords);
  }, [pickup, drop]);

  // FETCH ROUTE
  useEffect(() => {
    if (!isLoaded || !pickupCoords || !dropCoords) return;

    setLoading(true);

    const directionsService = new window.google.maps.DirectionsService();

    directionsService.route(
      {
        origin: { lat: pickupCoords[0], lng: pickupCoords[1] },
        destination: { lat: dropCoords[0], lng: dropCoords[1] },
        travelMode: window.google.maps.TravelMode.DRIVING,
      },
      (result, status) => {
        if (status === "OK" && result) {
          setDirectionsResult(result);

          // Update distance and duration
          const leg = result.routes[0].legs[0];
          setDistance((leg.distance.value / 1000).toFixed(2));
          setDuration((leg.duration.value / 60).toFixed(1));
        } else {
          // console.error("Directions request failed:", status);
          setDirectionsResult(null);
        }
        setLoading(false);
      },
    );
  }, [pickupCoords, dropCoords, isLoaded]);

  // ✅ Filter only active vehicles (status !== 0)
  const filteredVehicles = vehicles.filter(
    (car) => vehicleStatusMap[car.id] !== 0,
  );

  // ✅ Group only active vehicles
  const groupedVehicles = Object.values(
    filteredVehicles.reduce((acc, car) => {
      if (!acc[car.name]) {
        acc[car.name] = {
          name: car.name,
          seat: car.seat,
          km_price: car.km_price,
          img_url: car.img_url,
          vehicles: [],
        };
      }

      acc[car.name].vehicles.push(car);
      return acc;
    }, {}),
  );

  const getStateFromCoords = async (lat, lng) => {
    if (!window.google || !lat || !lng) return null;

    return new Promise((resolve) => {
      const geocoder = new window.google.maps.Geocoder();

      geocoder.geocode(
        {
          location: {
            lat: Number(lat),
            lng: Number(lng),
          },
        },
        (results, status) => {
          if (status === "OK" && results.length > 0) {
            const stateObj = results
              .flatMap((result) => result.address_components)
              .find((component) =>
                component.types.includes("administrative_area_level_1"),
              );

            resolve(stateObj ? stateObj.long_name : null);
          } else {
            console.warn("Reverse Geocode failed:", status);
            resolve(null);
          }
        },
      );
    });
  };

  useEffect(() => {
    if (!isLoaded || !dropCoords) return;

    const detectState = async () => {
      const [lat, lng] = dropCoords;

      const state = await getStateFromCoords(lat, lng);
      setDropState(state);
    };

    detectState();
  }, [dropCoords, isLoaded]);

  useEffect(() => {
    if (!isLoaded || !drop) return;

    const stateFromText = extractStateFromDropText(drop);

    if (stateFromText) {
      setDropState(stateFromText); // ✅ Auto-set state
      setShowStateModal(false); // ✅ Don't open modal
    } else if (dropCoords && !dropState) {
      setShowStateModal(true); // ✅ Open modal only if state not found
    }
  }, [dropCoords, dropState, isLoaded, drop]);

  const handleManualStateSave = () => {
    if (!manualState) return;

    setDropState(manualState);
    setShowStateModal(false);
  };

  const isOutsideAssam = () => {
    if (!dropState) return false;
    return dropState !== "Assam";
  };

  const [NoofDays, setNoofDays] = useState(0);

  useEffect(() => {
    if (pickupDate && dropDate) {
      const start = new Date(pickupDate);
      const end = new Date(dropDate);

      const diff = end - start;

      if (diff >= 0) {
        const days = Math.ceil(diff / (1000 * 60 * 60 * 24)) + 1;
        setNoofDays(days);
      } else {
        setNoofDays(0);
      }
    }
  }, [pickupDate, dropDate]);

  const calculateFare = (vehicle) => {
    if (!vehicle) return 0;

    const days = parseInt(NoofDays) || 0;
    const baseDailyPrice = parseFloat(vehicle.daily_price);

    let total = baseDailyPrice * days;

    // ✅ Add ₹500 per day if outside Assam
    if (isOutsideAssam()) {
      total += 500 * days;
    }

    return total.toFixed(0);
  };

  const calculateFareRange = (cars) => {
    if (!distance || cars.length === 0) return "0";

    const days = parseInt(NoofDays) || 0;

    const fares = cars.map((car) => {
      const baseDailyPrice = parseFloat(car.daily_price);
      const kmPrice = parseFloat(car.km_price);

      let total = baseDailyPrice * days;

      // ✅ Add ₹500 per day if outside Assam
      if (isOutsideAssam()) {
        total += 500 * days;
      }

      return total;
    });

    const min = Math.min(...fares).toFixed(0);
    const max = Math.max(...fares).toFixed(0);

    return min === max ? min : `${min} - ${max}`;
  };

  // EDIT RIDE
  const handleEditRide = async (data) => {
    setUpdatingTrip(true);

    setTimeout(() => {
      navigate("/choose-reserve-car", {
        state: data,
        replace: true,
      });

      setUpdatingTrip(false);
      setShowEditModal(false);
    }, 800);
  };

  const PREBOOK_FEE = 200;

  const handlePreBookingPayment = async () => {
    if (!user) {
      toast.error("Please login first");
      return;
    }

    if (!customerName || !customerPhone || !selectedCar) {
      toast.error("Missing details");
      return;
    }

    if (!pickupCoords) {
      toast.error("Pickup coordinates not loaded yet");
      return;
    }

    try {
      const options = {
        key: RAZORPAY_KEY, // Main Razorpay Key
        // key: "rzp_test_SEuHWyEAvt82zR",
        amount: PREBOOK_FEE * 100, // ₹200 in paise
        currency: "INR",
        name: "DibruGo",
        description: "Booking Fee",
        handler: async function (response) {
          // Razorpay Success Response
          const paymentId = response.razorpay_payment_id || "";

          toast.success("Payment successful!");

          // Call booking after payment success
          await handleConfirmBooking(paymentId);
        },
        prefill: {
          name: customerName,
          email: user?.email || "",
          contact: customerPhone,
        },
        theme: {
          color: "#0a0a0a",
        },
      };

      if (!window.Razorpay) {
        toast.error("Payment service not loaded");
        return;
      }

      const razorpay = new window.Razorpay(options);
      razorpay.open();
    } catch (err) {
      // console.error("Razorpay Error:", err);
      toast.error("Payment failed");
    }
  };

  let pickupTimestamp = null;

  if (pickupDate && pickupTime) {
    const dateTime = new Date(`${pickupDate}T${pickupTime}`);
    pickupTimestamp = Timestamp.fromDate(dateTime);
  }

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return "N/A";
    return new Date(timestamp.seconds * 1000).toLocaleString("en-IN", {
      dateStyle: "medium",
      timeStyle: "short",
    });
  };

  const getCurrentDateTime = () => {
    const now = new Date();
    return now.toLocaleString("en-GB");
  };

  const sendSMSReminder = async (booking) => {
    const bookedAt = getCurrentDateTime(booking.created_at);
    const pickupAt = formatTimestamp(booking.pickupTime);

    const message = `🔔 New Booking (Reserve)
     
Booking Time: ${bookedAt}
Pick Up Time: ${pickupAt}
  
Pickup: ${booking.pickup}
Drop: ${booking.drop}

Passengers: ${booking.passengers}
Price: ₹${booking.total_price} + ₹${
      isOutsideAssam()
        ? parseFloat(selectedCar.km_price) + 4
        : selectedCar.km_price
    }/km
          

Booking Details: https://dibrugo.com/admin-live-bookings`;

    try {
      const response = await fetch(SMS_BASE_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "x-api-key": SMS_API_KEY, // 👈 paste your key here
        },
        body: JSON.stringify({
          from: SMS_FROM_NUMBER, // 👈 put SIM number inside Android phone
          to: "+917576825040",
          // to: "+917576825040" ,
          content: message,
        }),
      });

      const data = await response.json();

      if (response.ok) {
        // toast.success("SMS Sent Successfully");
        console.log("SMS Response sent");
      } else {
        // toast.error("SMS Failed");
        console.log("Error:", data);
      }
    } catch (error) {
      console.error("SMS Error:", error);
      // toast.error("SMS Error");
    }
  };

  const [otpSent, setOtpSent] = useState(false);
  const [phoneVerified, setPhoneVerified] = useState(false);

  const [otp, setOtp] = useState("");
  const [confirmationResult, setConfirmationResult] = useState(null);

  const [loadingOTP, setLoadingOTP] = useState(false);
  const [loadingVerify, setLoadingVerify] = useState(false);

  const [timer, setTimer] = useState(0);
  // ⏳ Timer countdown
  useEffect(() => {
    let interval;
    if (timer > 0) {
      interval = setInterval(() => {
        setTimer((prev) => prev - 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [timer]);

  // Setup reCAPTCHA
  const setupRecaptcha = () => {
    if (!window.recaptchaVerifier) {
      window.recaptchaVerifier = new RecaptchaVerifier(
        auth,
        "recaptcha-container",
        {
          size: "invisible",
        },
      );
    }
  };

  // 📩 Send OTP
  const sendOTP = async () => {
    if (!customerPhone) return alert("Enter phone number");

    // 👉 Ensure +91 is added
    let formattedPhone = customerPhone.trim();

    if (!formattedPhone.startsWith("+91")) {
      formattedPhone = "+91" + formattedPhone;
    }

    try {
      setLoadingOTP(true);
      setupRecaptcha();

      const appVerifier = window.recaptchaVerifier;

      const confirmation = await signInWithPhoneNumber(
        auth,
        formattedPhone,
        appVerifier,
      );

      setConfirmationResult(confirmation);
      window.confirmationResult = confirmation;

      setTimer(60); // ⏳ start 60 sec timer

      toast.success("OTP Sent!");
      setOtpSent(true);
    } catch (error) {
      console.error("OTP Error:", error);
    } finally {
      setLoadingOTP(false);
    }
  };

  // 🔐 Verify OTP
  const verifyOTP = async () => {
    if (!confirmationResult) {
      toast.error("Please request OTP first");
      return;
    }

    try {
      setLoadingVerify(true);

      const result = await confirmationResult.confirm(otp);

      const user = result.user;

      // Reference to Users collection
      const userRef = doc(db, "Users", user.uid);

      // Check if user already exists
      const userSnap = await getDoc(userRef);

      if (!userSnap.exists()) {
        // Save new user
        await setDoc(userRef, {
          uid: user.uid,
          phone: user.phoneNumber,
          createdAt: new Date(),
        });
      }

      toast.success("Login Successful!");
      setPhoneVerified(true);
      setOtpSent(false);
    } catch (error) {
      console.error("Invalid OTP", error);
      toast.error("Invalid OTP");
    } finally {
      setLoadingVerify(false);
    }
  };

  // CONFIRM BOOKING
  const handleConfirmBooking = async (paymentRef = "") => {
    if (typeof paymentRef !== "string") {
      paymentRef = ""; // prevent event object being stored
    }

    if (!customerName || !customerPhone) {
      toast.error("Missing details");
      return;
    }

    setSavingBooking(true);

    try {
      const latitude = pickupCoords[0];
      const longitude = pickupCoords[1];

      const drop_latitude = dropCoords[0];
      const drop_longitude = dropCoords[1];

      if (!pickupCoords || pickupCoords.length < 2) {
        toast.error("Pickup coordinates missing");
        return;
      }

      if (!dropCoords) {
        toast.error("Drop coordinates not loaded yet");
        return;
      }

      if (!selectedCar) {
        toast.error("Please select a vehicle");
        return;
      }

      // ✅ 1. Update Users collection
      if (user?.uid) {
        await setDoc(
          doc(db, "Users", user.uid),
          {
            name: customerName,
          },
          { merge: true }, // prevents overwriting other fields
        );
      }

      const bookingData = {
        customer_id: user?.uid || "",
        customer_name: customerName,
        customer_phone: customerPhone,
        passengers,

        pickup,
        drop,
        dropState,

        pickup_lat: latitude,
        pickup_lng: longitude,

        drop_lat: drop_latitude,
        drop_lng: drop_longitude,

        isScheduled: true,
        pickupTime: pickupTimestamp,

        vehicle_id: selectedCar.id,
        NoOfDays: NoofDays,
        daily_rate: parseFloat(selectedCar.daily_price),
        km_price: parseInt(selectedCar.km_price),
        // km_price: isOutsideAssam()
        //   ? parseFloat(selectedCar.km_price)
        //   : selectedCar.km_price,
        total_price: parseFloat(calculateFare(selectedCar)),

        prebookingFeePaid: true,
        preBookingFee: 200,
        razorpay_payment_ref: paymentRef || "",

        status: 0,
        driver_id: null,
        created_at: serverTimestamp(),
        booking_type: "Reserve",
      };

      // console.log(bookingData, "dwsdasd");

      await addDoc(collection(db, "LiveBookings"), bookingData);
      // ✅ Send SMS to admin
      sendSMSReminder(bookingData);
      toast.success("Booking Confirmed!");
      setTimeout(() => {
        navigate("/my-ride-details");
      }, 1500);
    } catch (err) {
      console.error("Booking error:", err);
      toast.error("Booking failed");
    }

    setSavingBooking(false);
  };

  return (
    <>
      <GoToTop />

      {updatingTrip && (
        <div className="position-fixed top-0 start-0 w-100 h-100 bg-light bg-opacity-75 d-flex justify-content-center align-items-center z-3 loader">
          <div className="bg-white px-4 py-3 rounded-4 shadow fw-bold">
            Updating route...
          </div>
        </div>
      )}

      <div className="container py-3" style={{ marginTop: "75px" }}>
        {/* HEADER */}

        <div className="uber-summary-card">
          <div className="uber-locations">
            <div className="location-row">
              <div className="dot pickup-dot"></div>
              <div className="location-text">
                <span className="label">Pickup</span>
                <span className="value">{pickup}</span>
              </div>
            </div>

            <div className="short-vertical-line"></div>

            <div className="location-row">
              <div className="square drop-square"></div>
              <div className="location-text">
                <span className="label">Drop</span>
                <span className="value">{drop}</span>
              </div>
            </div>
          </div>
        </div>

        <div className="uber-date-card mt-2">
          <div className="uber-date-row">
            <div>
              <div className="date-label">Pickup</div>
              <div className="date-value">
                {new Date(pickupDate).toLocaleDateString("en-IN", {
                  day: "numeric",
                  month: "short",
                  year: "numeric",
                })}
              </div>
              <div className="date-sub">
                {new Date(`1970-01-01T${pickupTime}`).toLocaleTimeString(
                  "en-IN",
                  {
                    hour: "2-digit",
                    minute: "2-digit",
                  },
                )}
              </div>
            </div>
          </div>

          <div className="divider"></div>

          <div className="uber-date-row">
            <div>
              <div className="date-label">Return</div>
              <div className="date-value">
                {new Date(dropDate).toLocaleDateString("en-IN", {
                  day: "numeric",
                  month: "short",
                  year: "numeric",
                })}
              </div>
              <div className="days-badge">
                ( {NoofDays} {NoofDays > 1 ? "Days" : "Day"} )
              </div>
            </div>
          </div>
        </div>

        <div className="p-2">
          <h6 className="fw-bold mb-2">Choose a Ride</h6>

          {!dropState && vehicles.length < 1 && (
            <p className="text-muted text-center">Loading vehicles...</p>
          )}

          {groupedVehicles.map((group) => {
            // Filter cars that can actually be used
            const filteredVehicles = vehicles.filter(
              (car) => vehicleStatusMap[car.id] !== 0,
            );

            const activeVehicles = group.vehicles.filter((car) =>
              filteredVehicles.includes(car),
            );

            const fareRange = calculateFareRange(activeVehicles);

            return (
              <div
                key={group.name}
                onClick={() => {
                  if (activeVehicles.length > 0) {
                    setSelectedCar(activeVehicles[0]);
                  }
                }}
                className={`d-flex justify-content-between align-items-center p-3 rounded-3 mb-2 border uber-ride-card ${
                  selectedCar?.name === group.name ? "active" : ""
                } ${activeVehicles.length === 0 ? "disabled-ride" : ""}`}
                style={{
                  opacity: activeVehicles.length === 0 ? 0.5 : 1,
                  pointerEvents: activeVehicles.length === 0 ? "none" : "auto",
                }}
              >
                <div className="d-flex align-items-center gap-3">
                  <img
                    src={group.img_url}
                    className="img-fluid"
                    style={{ width: "85px", mixBlendMode: "darken" }}
                  />

                  <div>
                    <h6 className="mb-0">{group.name} </h6>
                    <small
                      className="mb-0"
                      style={{ fontSize: "12px", marginTop: "-5px" }}
                    >
                      <i>{group.seat} seater AC Cab</i>
                    </small>
                  </div>
                </div>

                <div style={{ textAlign: "right" }}>
                  <h6 className="mb-0">
                    <s
                      style={{ fontSize: "12px", color: "grey" }}
                      className="me-1"
                    >
                      ₹
                      {fareRange
                        ? (Number(fareRange) * 1.15).toFixed(0)
                        : "0.00"}
                    </s>
                    <b style={{ fontSize: "16px" }}>
                      {formatINR(fareRange) || "0.00"}
                    </b>
                  </h6>
                  <p
                    className="mb-0"
                    style={{ fontSize: "13px", color: "grey" }}
                  >
                    + ₹
                    {isOutsideAssam()
                      ? parseFloat(group.km_price) + 4
                      : group.km_price}{" "}
                    per km
                  </p>
                </div>
              </div>
            );
          })}
          <br />
          <br />

          {selectedCar && (
            <div className="sticky-confirm-footer">
              <button
                className="btn btn-dark w-100 mt-3 py-3 rounded-3 fw-bold"
                onClick={() => setShowBookingModal(true)}
              >
                Book {selectedCar.name}
                {/* - ₹{calculateFare(selectedCar)} */}
              </button>
            </div>
          )}
        </div>
      </div>

      <ReserveTripModal
        showReserve={showEditModal}
        onCloseReserve={() => setShowEditModal(false)}
        onSearchReserveRide={handleEditRide}
        initialPickup={pickup}
        initialDrop={drop}
        initialScheduled={isScheduled}
        initialPickupDate={pickupDate}
        initialPickupTime={pickupTime}
        initialDropDate={dropDate}
        loading={updatingTrip}
      />

      {showBookingModal && (
        <div
          className="bottom-modal-overlay"
          onClick={() => setShowBookingModal(false)}
        >
          <div className="bottom-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header mb-2">
              <h5 className="fw-bold mb-0">Confirm your ride</h5>
              <button
                className="btn-close"
                onClick={() => setShowBookingModal(false)}
              />
            </div>

            <div className="uber-summary-card">
              <div className="uber-locations">
                <div className="location-row">
                  <div className="dot pickup-dot"></div>
                  <div className="location-text">
                    <span className="label">Pickup</span>
                    <span className="value">{pickup}</span>
                  </div>
                </div>

                <div className="short-vertical-line"></div>

                <div className="location-row">
                  <div className="square drop-square"></div>
                  <div className="location-text">
                    <span className="label">Drop</span>
                    <span className="value">{drop}</span>
                  </div>
                </div>
              </div>
            </div>
            <div
              className="mt-2 mb-0 uber-ride-card"
              style={{ position: "relative" }}
            >
              <div className="d-flex gap-2 align-items-center">
                <img
                  src={selectedCar.img_url}
                  className="img-fluid"
                  style={{
                    width: "120px",
                    height: "70px",
                    objectFit: "contain",
                  }}
                />
                <div>
                  <div
                    className="meta-badge"
                    style={{ position: "absolute", right: "10px" }}
                  >
                    {NoofDays} {NoofDays > 1 ? "Days" : "Day"}
                  </div>
                  <h6 className="mt-0 mb-0">{selectedCar.name}</h6>
                  <p className="mb-0" style={{ fontSize: "13px" }}>
                    {selectedCar.seat} seater AC Cab
                  </p>
                  <p className="mb-1" style={{ fontSize: "13px" }}>
                    Fare: <b>₹{calculateFare(selectedCar)}.00</b> +{" "}
                    <b>
                      ₹
                      {isOutsideAssam()
                        ? parseFloat(selectedCar.km_price) + 4
                        : selectedCar.km_price}{" "}
                      /km
                    </b>
                  </p>
                </div>
              </div>
            </div>

            <div style={{ maxHeight: "265px", overflowY: "auto" }}>
              <label className="form-label">Passenger Name</label>
              <input
                type="text"
                required
                className="modal-input mt-0 mb-1"
                placeholder="Customer Name"
                value={customerName}
                onChange={(e) => setCustomerName(e.target.value)}
              />
              <label className="form-label">
                Mobile Number<span style={{ color: "red" }}>*</span>
              </label>
              <div className="d-flex gap-1">
                <input
                  type="tel"
                  className="modal-input mt-0 mb-1"
                  placeholder="Mobile Number"
                  value={customerPhone}
                  readOnly={user}
                  disabled={phoneVerified}
                  onChange={(e) => setCustomerPhone(e.target.value)}
                />
                {!otpSent && !phoneVerified && !user && (
                  <button
                    className="btn btn-dark"
                    onClick={sendOTP}
                    style={{
                      width: "180px",
                      height: "50px",
                      borderRadius: "10px",
                    }}
                    disabled={loadingOTP}
                  >
                    {loadingOTP ? "Sending..." : "Send OTP"}
                  </button>
                )}
              </div>

              {otpSent &&
                !phoneVerified &&
                (timer > 0 ? (
                  <p
                    style={{ fontSize: "13px", color: "red" }}
                    className="mb-0 ms-2"
                  >
                    Resend in {timer}s
                  </p>
                ) : (
                  <p
                    className="mb-0 ms-2"
                    onClick={!loadingOTP ? sendOTP : null}
                    style={{
                      fontSize: "13px",
                      color: "red",
                      cursor: loadingOTP ? "not-allowed" : "pointer",
                      opacity: loadingOTP ? 0.5 : 1,
                    }}
                  >
                    Resend OTP
                  </p>
                ))}
              <div id="recaptcha-container"></div>

              {/* OTP Section */}
              {otpSent && !phoneVerified && (
                <div className="mt-2">
                  <div className="d-flex gap-2">
                    <input
                      type="number"
                      className="modal-input mt-0"
                      placeholder="Enter OTP"
                      value={otp}
                      onChange={(e) => setOtp(e.target.value)}
                    />

                    <button
                      className="btn btn-dark"
                      style={{
                        width: "180px",
                        height: "50px",
                        borderRadius: "10px",
                      }}
                      disabled={!confirmationResult || loadingVerify}
                      onClick={verifyOTP}
                    >
                      {loadingVerify ? "Verifying..." : "Verify OTP"}
                    </button>
                  </div>
                </div>
              )}

              <label className="form-label">Number of Passengers</label>
              <div className="d-flex align-items-center justify-content-between border rounded-3 px-2 py-2">
                <span>Passengers</span>

                <div className="d-flex align-items-center gap-3">
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-dark rounded-circle px-2"
                    onClick={() =>
                      setPassengers((prev) => Math.max(1, prev - 1))
                    }
                  >
                    −
                  </button>

                  <span className="fw-bold">{passengers}</span>

                  <button
                    type="button"
                    className="btn btn-sm btn-outline-dark rounded-circle px-2"
                    onClick={() => setPassengers((prev) => prev + 1)}
                  >
                    +
                  </button>
                </div>
              </div>
              <div
                className="uber-summary-card mt-2"
                style={{ marginBottom: "10px" }}
              >
                <h6>Booking Summary</h6>
                <div className="d-flex justify-content-between">
                  <small>Booking Fee :</small>
                  <b>₹200.00</b>
                </div>
              </div>
            </div>

            <div className="mt-3" style={{ position: "sticky", bottom: "2px" }}>
              <button
                className="btn btn-dark w-100 py-3 fw-bold"
                disabled={savingBooking || !user}
                onClick={handlePreBookingPayment}
              >
                {savingBooking
                  ? "Confirming payment..."
                  : !user
                    ? "Login to Confirm Pre-Booking"
                    : "Proceed to Pay"}
              </button>
            </div>
          </div>
        </div>
      )}

      {showStateModal && (
        <div className="bottom-modal-overlay">
          <div className="bottom-modal">
            <div className="modal-header">
              <h5>Select Drop State</h5>
            </div>

            <div className="mt-3">
              <div className="card p-3 highlight-card">
                <p className="mb-0">
                  <FaLocationDot className="icon me-1" />
                  {drop}
                </p>
              </div>
              <label className="form-label">Choose State</label>

              <select
                className="form-select mb-3"
                value={manualState}
                onChange={(e) => setManualState(e.target.value)}
              >
                <option value="">Select State</option>
                <option value="Assam">Assam</option>
                <option value="Arunachal Pradesh">Arunachal Pradesh</option>
                <option value="Meghalaya">Meghalaya</option>
                <option value="Manipur">Manipur</option>
                <option value="Mizoram">Mizoram</option>
                <option value="Nagaland">Nagaland</option>
                <option value="Tripura">Tripura</option>
              </select>

              <button
                className="btn btn-dark w-100 py-3"
                fw-bold
                onClick={handleManualStateSave}
                disabled={!manualState}
              >
                Confirm State
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default ChooseCar;
